import { useRef, useState } from 'react';
import type { CSSProperties, DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Icon } from '@vault/ui';
import { api, ApiError } from '../api/client.js';
import { useT } from '../i18n/index.js';

type Fmt = 'onetab' | 'bookmarks' | 'sessionBuddy';

interface ImportResult {
  sweepId: string;
  imported: number;
  skippedDuplicates: number;
}

const SOURCES: Array<{ fmt: Fmt; letter: string; name: string; sub: string; favIndex: number }> = [
  { fmt: 'onetab', letter: 'O', name: 'OneTab', sub: 'TXT EXPORT', favIndex: 5 },
  { fmt: 'sessionBuddy', letter: 'S', name: 'Session Buddy', sub: 'JSON', favIndex: 3 },
  { fmt: 'bookmarks', letter: '★', name: 'Bookmarks', sub: 'HTML', favIndex: 7 },
];

function fmtFromName(name: string): Fmt | null {
  const n = name.toLowerCase();
  if (n.endsWith('.txt')) return 'onetab';
  if (n.endsWith('.json')) return 'sessionBuddy';
  if (n.endsWith('.html') || n.endsWith('.htm')) return 'bookmarks';
  return null;
}

function fmtFromContent(text: string): Fmt {
  const t = text.trimStart();
  if (t.startsWith('<')) return 'bookmarks';
  if (t.startsWith('{') || t.startsWith('[')) return 'sessionBuddy';
  return 'onetab';
}

function readText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('read_failed'));
    reader.readAsText(file);
  });
}

async function runImport(file: File): Promise<ImportResult> {
  const text = await readText(file);
  const fmt = fmtFromName(file.name) ?? fmtFromContent(text);
  if (fmt === 'onetab') return api.import.onetab(text);
  if (fmt === 'bookmarks') return api.import.bookmarks(text);
  return api.import.sessionBuddy(text);
}

/**
 * Import wizard: drop or pick an export (OneTab .txt, Session Buddy .json,
 * bookmarks .html), we detect the format, post it to the vault, and show what
 * landed. Duplicates are merged server-side as it goes.
 */
export function ImportPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);

  const importMut = useMutation({
    mutationFn: (f: File) => runImport(f),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sweeps'] });
      void qc.invalidateQueries({ queryKey: ['usage'] });
      void qc.invalidateQueries({ queryKey: ['dedup'] });
    },
  });

  const detected = file ? fmtFromName(file.name) : null;

  function choose(f: File | null) {
    if (!f) return;
    importMut.reset();
    setFile(f);
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) choose(f);
  }

  function reset() {
    setFile(null);
    importMut.reset();
    if (inputRef.current) inputRef.current.value = '';
  }

  const step: 1 | 2 | 3 = importMut.isSuccess ? 3 : importMut.isPending ? 2 : 1;

  return (
    <div style={page}>
      <style>{IMPORT_CSS}</style>
      <input
        ref={inputRef}
        type="file"
        accept=".txt,.json,.html,.htm"
        style={{ display: 'none' }}
        onChange={(e) => choose(e.target.files?.[0] ?? null)}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <h1 style={heading}>{t('import.title')}</h1>
        <Stepper step={step} />
      </div>

      <div className="tvim-cols" style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
        {/* left — source */}
        <div style={{ flex: 1.2, minWidth: 280, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div
            className="tvim-drop"
            data-active={dragging ? 'true' : undefined}
            style={dropzone}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
          >
            <span style={dropIcon}>
              <Icon name="download" size={20} color="var(--primary)" />
            </span>
            <span style={{ fontSize: 14.5, fontWeight: 600 }}>{t('import.drop.title')}</span>
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
              {t('import.drop.body')} <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{t('import.drop.browse')}</span>
            </span>
            {file && (
              <span style={filePill} onClick={(e) => e.stopPropagation()}>
                <Icon name="list" size={12} color="var(--text-3)" />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-2)' }}>
                  {file.name} · {formatSize(file.size)}
                </span>
                <span onClick={reset} style={{ display: 'inline-flex', cursor: 'pointer' }}>
                  <Icon name="x" size={10} color="var(--text-3)" />
                </span>
              </span>
            )}
          </div>

          <div className="tvim-sources" style={sourceGrid}>
            {SOURCES.map((s) => {
              const active = detected === s.fmt;
              return (
                <span
                  key={s.fmt}
                  className="tvim-source"
                  data-active={active ? 'true' : undefined}
                  style={{ ...sourceChip, ...(active ? sourceChipActive : null) }}
                  onClick={() => inputRef.current?.click()}
                >
                  <span style={{ ...sourceMark, background: `var(--fav-${s.favIndex})` }}>{s.letter}</span>
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600 }}>{s.name}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 8.5, color: 'var(--text-3)' }}>{s.sub}</span>
                  </span>
                  {active && (
                    <span style={sourceCheck}>
                      <Icon name="check" size={8} color="#fff" strokeWidth={2.6} />
                    </span>
                  )}
                </span>
              );
            })}
          </div>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-3)' }}>
            TOBY IMPORT COMING SOON
          </span>
        </div>

        {/* right — action / progress / result */}
        <div style={{ flex: 1, minWidth: 280, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {step === 1 && !importMut.isError && (
            <div style={panel}>
              <span style={eyebrow}>{t('import.readyEyebrow')}</span>
              {file ? (
                <>
                  <span style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.55 }}>
                    We’ll read <b style={{ color: 'var(--text-1)' }}>{file.name}</b> as a{' '}
                    <b style={{ color: 'var(--text-1)' }}>{detected ? labelFor(detected) : 'auto-detected'}</b> export and merge any
                    duplicates automatically.
                  </span>
                  <Button icon={<Icon name="download" size={14} color="currentColor" />} onClick={() => importMut.mutate(file)}>
                    Import this file
                  </Button>
                </>
              ) : (
                <span style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.55 }}>
                  Choose an export on the left. Nothing is imported until you confirm.
                </span>
              )}
            </div>
          )}

          {step === 2 && (
            <div style={panel}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>Importing…</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)' }}>DEDUPING AS WE GO</span>
              </div>
              <span className="tv-skeleton" style={{ display: 'block', height: 6, borderRadius: 3 }} />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--text-3)' }}>
                READING {file?.name ?? 'FILE'} · THIS ONLY TAKES A MOMENT
              </span>
            </div>
          )}

          {step === 3 && importMut.data && (
            <div style={resultCard}>
              <span style={resultIcon}>
                <Icon name="check" size={16} color="var(--success)" strokeWidth={1.9} />
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--success)' }}>
                  Done: {importMut.data.imported.toLocaleString()} tabs imported
                </span>
                <span style={{ fontSize: 11.5, color: 'var(--text-2)' }}>
                  {importMut.data.skippedDuplicates.toLocaleString()} duplicates merged · saved to a new import session
                </span>
              </span>
              <span style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                <button type="button" className="tvim-link" onClick={() => navigate('/timeline')}>
                  View in Timeline <Icon name="external" size={11} color="currentColor" />
                </button>
                <button type="button" className="tvim-link-muted" onClick={reset}>
                  Import another file
                </button>
              </span>
            </div>
          )}

          {importMut.isError && (
            <div style={errorCard}>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger)' }}>We couldn’t import that file</span>
              <span style={{ fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.55 }}>{errorText(importMut.error)}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                {file && (
                  <Button variant="danger" size="sm" onClick={() => importMut.mutate(file)}>
                    Try again
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={reset}>
                  Choose another
                </Button>
              </div>
            </div>
          )}

          <div style={reassurePanel}>
            <Icon name="lock" size={13} color="var(--text-3)" />
            <span style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.5 }}>
              Your file is parsed and saved straight to your vault — duplicates merge automatically, and your saved
              pages stay private.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stepper({ step }: { step: 1 | 2 | 3 }) {
  const items: Array<[number, string]> = [
    [1, '01 SOURCE'],
    [2, '02 IMPORT'],
    [3, '03 DONE'],
  ];
  return (
    <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.1em' }}>
      {items.map(([n, label], i) => {
        const done = step > n;
        const active = step === n;
        return (
          <span key={n} style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            {i > 0 && <span style={{ width: 20, height: 1, background: 'var(--line-strong)' }} />}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: done ? 'var(--success)' : active ? 'var(--primary)' : 'var(--text-3)' }}>
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 8,
                  fontWeight: 700,
                  color: done || active ? '#fff' : 'var(--text-3)',
                  background: done ? 'var(--success)' : active ? 'var(--primary)' : 'transparent',
                  border: done || active ? 'none' : '1px solid var(--line-strong)',
                }}
              >
                {done ? <Icon name="check" size={8} color="#fff" strokeWidth={2.6} /> : n}
              </span>
              {label}
            </span>
          </span>
        );
      })}
    </span>
  );
}

function labelFor(fmt: Fmt): string {
  if (fmt === 'onetab') return 'OneTab';
  if (fmt === 'bookmarks') return 'bookmarks';
  return 'Session Buddy';
}
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 413) return 'That file is too large. Try splitting the export into smaller pieces.';
    if (err.status === 400 || err.status === 422)
      return 'We couldn’t read that file as a supported export. Make sure it’s a OneTab .txt, Session Buddy .json, or bookmarks .html.';
    return 'The import failed on our side. Please try again in a moment.';
  }
  return 'Something went wrong reading the file. Check the format and try again.';
}

const page: CSSProperties = {
  maxWidth: 1100,
  margin: '0 auto',
  padding: '24px 28px',
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
};
const heading: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, letterSpacing: '-0.025em',
};
const dropzone: CSSProperties = {
  border: '1.5px dashed var(--line-primary)',
  background: 'var(--primary-tint)',
  borderRadius: 16,
  padding: '30px 24px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
  textAlign: 'center',
  cursor: 'pointer',
};
const dropIcon: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 46,
  height: 46,
  borderRadius: 14,
  background: 'var(--surface-1)',
  border: '1px solid var(--line-strong)',
};
const filePill: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  background: 'var(--surface-1)',
  border: '1px solid var(--line-strong)',
  borderRadius: 9,
  padding: '7px 12px',
  marginTop: 6,
};
const sourceGrid: CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 };
const sourceChip: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  background: 'var(--surface-1)',
  border: '1px solid var(--line)',
  borderRadius: 12,
  padding: '12px 14px',
  cursor: 'pointer',
};
const sourceChipActive: CSSProperties = { border: '1px solid var(--line-primary)', background: 'var(--primary-tint)' };
const sourceMark: CSSProperties = {
  width: 26,
  height: 26,
  borderRadius: 8,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 11,
  fontWeight: 700,
  color: '#fff',
  flexShrink: 0,
};
const sourceCheck: CSSProperties = {
  marginLeft: 'auto',
  width: 15,
  height: 15,
  borderRadius: '50%',
  background: 'var(--primary)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const panel: CSSProperties = {
  background: 'var(--surface-1)',
  border: '1px solid var(--line)',
  borderRadius: 14,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 11,
};
const eyebrow: CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.14em', color: 'var(--text-3)' };
const resultCard: CSSProperties = {
  background: 'var(--success-tint)',
  border: '1px solid var(--success-line)',
  borderRadius: 14,
  padding: 16,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};
const resultIcon: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 34,
  height: 34,
  borderRadius: 11,
  background: 'var(--surface-1)',
  flexShrink: 0,
};
const errorCard: CSSProperties = {
  background: 'var(--surface-1)',
  border: '1px solid var(--danger-line)',
  borderRadius: 14,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};
const reassurePanel: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  background: 'var(--surface-1)',
  border: '1px solid var(--line)',
  borderRadius: 12,
  padding: '10px 13px',
};

const IMPORT_CSS = `
.tvim-drop{transition:border-color .15s var(--ease),background .15s var(--ease)}
.tvim-drop:hover,.tvim-drop[data-active="true"]{border-color:var(--primary);background:var(--primary-tint)}
.tvim-source{transition:border-color .15s var(--ease)}
.tvim-source:hover{border-color:var(--line-strong)}
.tvim-link{display:inline-flex;align-items:center;gap:6px;background:transparent;border:none;cursor:pointer;
  font-family:var(--font-body);font-size:12px;font-weight:600;color:var(--primary);white-space:nowrap}
.tvim-link:hover{color:var(--primary-hover)}
.tvim-link-muted{background:transparent;border:none;cursor:pointer;font-family:var(--font-body);font-size:11px;
  font-weight:600;color:var(--text-3)}
.tvim-link-muted:hover{color:var(--text-1)}
@media (max-width:760px){.tvim-cols{flex-direction:column}}
@media (max-width:420px){.tvim-sources{grid-template-columns:1fr}}
`;
